import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { BuildingIcon, Heading, NakedButton, Text } from '@weave/design-system';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Page } from '../shared';
import { useOrgHierarchy } from '../../apis/identity-search/queries';
import { setCurrentLocationId } from '../../redux/actions/location';
import { addOrgToHistory } from '../../redux/actions/org/org-history/org-history.action';
import { addOrgTab, setCurrentOrgId } from '../../redux/actions/org/org-tabs';
import { OrgHistoryModel } from '../../models/org-history.model';
import { OrgHierarchyTab } from './org-hierarchy-tab/org-hierarchy-tab.component';
import { OrgSsoTab } from './org-sso-tab/org-sso-tab.component';

type OrgDetailsTab = 'hierarchy' | 'sso';

const NAV_ITEMS: { id: OrgDetailsTab; label: string }[] = [
  { id: 'hierarchy', label: 'Hierarchy' },
  { id: 'sso', label: 'Single Sign-On (SSO)' },
];

const layout = css`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: calc(100vh - 48px);

  @media only screen and (max-width: 900px) {
    flex-direction: column;
  }
`;

const sidebar = css`
  display: flex;
  flex-direction: column;
  flex: 0 0 320px;
  width: 320px;
  padding: ${theme.spacing(3)};
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.heavy};

  @media only screen and (max-width: 900px) {
    width: 100%;
    flex: 0 0 auto;
  }
`;

const sidebarTitle = css`
  margin: 0 0 ${theme.spacing(2)};
`;

const orgIdentity = css`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing(1)};
  cursor: pointer;

  h2 {
    margin: 0;
    word-break: break-word;
  }
`;

const orgIcon = css`
  flex: 0 0 auto;
  margin-top: ${theme.spacing(0.5)};
`;

const orgIdStyle = css`
  display: block;
  margin-top: ${theme.spacing(1)};
  overflow-wrap: anywhere;
  cursor: pointer;
`;

const navigation = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
  margin-top: ${theme.spacing(3)};
  padding-top: ${theme.spacing(2)};
  border-top: 1px solid ${theme.colors.gray300};
`;

const navButton = css`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: ${theme.spacing(1, 2)};
  border-left: 4px solid transparent;
  border-radius: ${theme.borderRadius.small};
  text-align: left;
  color: ${theme.colors.gray600};
  font-size: ${theme.fontSize(14)};
  font-weight: normal;
  :hover {
    background-color: ${theme.colors.gray200};
  }
`;

const navButtonActive = css`
  color: ${theme.colors.weaveBlue};
  font-weight: bold;
  border-left-color: ${theme.colors.weaveBlue};
  background-color: ${theme.colors.gray200};
  :hover {
    background-color: ${theme.colors.gray200};
  }
`;

const content = css`
  flex: 1 1 auto;
  min-width: 0;
  margin: ${theme.spacing(2)};
`;

// OrgDetailsPage is the landing page shown after an organization is selected from the
// location lookup widget. A vertical nav on the left switches between the org's
// hierarchy (via IAM's GetOrgHierarchy gRPC endpoint, see iam/internal/grpc/org.go)
// and its SSO configuration. The hierarchy tab lets the user drill into any of the
// org's locations, which navigates to that location's dashboard.
export const OrgDetailsPage = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const dispatch = useDispatch();
  const { data, isLoading } = useOrgHierarchy(orgId);
  const [orgIdCopied, setOrgIdCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<OrgDetailsTab>('hierarchy');

  // Record this org in history (mirrors location history) once its name resolves,
  // so it can be shown alongside recently viewed locations on the home page. Also add
  // an org tab to the header (mirroring location tabs) and mark it as the active tab.
  useEffect(() => {
    if (data?.org?.orgId && data.org.name) {
      dispatch(
        addOrgToHistory(
          new OrgHistoryModel({ orgId: data.org.orgId, name: data.org.name })
        )
      );
      dispatch(addOrgTab({ OrgId: data.org.orgId, Name: data.org.name }));
      dispatch(setCurrentOrgId(data.org.orgId));
    }
  }, [data?.org?.orgId, data?.org?.name, dispatch]);

  const goToLocation = (locationId?: string) => {
    if (!locationId) {
      return;
    }
    dispatch(setCurrentLocationId(locationId));
  };

  const copyOrgId = () => {
    setOrgIdCopied(true);
    setTimeout(() => setOrgIdCopied(false), 2000);
  };

  const divisions = data?.divisions ?? [];

  const firstLocationId = divisions.flatMap((d) => d.locations ?? [])[0]?.locationId;

  return (
    <div css={layout}>
      <aside css={sidebar}>
        <Heading level={2} css={sidebarTitle}>
          Organization Info
        </Heading>
        <CopyToClipboard text={data?.org?.name || 'Organization'}>
          <div css={orgIdentity} title="Copy organization name">
            <BuildingIcon css={orgIcon} />
            <Heading level={2}>{data?.org?.name || 'Organization'}</Heading>
          </div>
        </CopyToClipboard>
        {orgId && (
          <CopyToClipboard text={orgId} onCopy={copyOrgId}>
            <Text
              css={orgIdStyle}
              color="light"
              size="medium"
              title="Copy organization ID"
            >
              {orgId}
              {orgIdCopied && (
                <Text as="span" color="success">
                  {' '}
                  Copied!
                </Text>
              )}
            </Text>
          </CopyToClipboard>
        )}

        <nav css={navigation}>
          {NAV_ITEMS.map((item) => (
            <NakedButton
              key={item.id}
              css={[navButton, activeTab === item.id && navButtonActive]}
              onClick={() => setActiveTab(item.id)}
            >
              {item.label}
            </NakedButton>
          ))}
        </nav>
      </aside>

      <Page
        title={data?.org?.name || 'Organization'}
        hideHeader
        loading={isLoading}
        containerStyle={content}
      >
        {activeTab === 'hierarchy' && (
          <OrgHierarchyTab divisions={divisions} onSelectLocation={goToLocation} />
        )}
        {activeTab === 'sso' && (
          <OrgSsoTab orgName={data?.org?.name} locationId={firstLocationId} />
        )}
      </Page>
    </div>
  );
};
